// Teams Table Component - Following Tasks Table Pattern

import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Shield,
  Code,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
  Search,
  X,
  Trash2,
  Eye,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import type { Team, TeamFilters, User, Permission } from '@/types/teams';
import { ViewTeamUsersModal } from './ViewTeamUsersModal';
import { ViewTeamPermissionsModal } from './ViewTeamPermissionsModal';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: Users,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface TeamsTableProps {
  teams: Team[];
  allUsers: User[];
  permissions: Permission[];
  filters: TeamFilters;
  onFiltersChange: (filters: TeamFilters) => void;
  onTeamClick: (team: Team) => void;
  onDeleteTeams: (teamIds: string[]) => void;
  onRefresh: () => void;
}

// Helper function to filter teams by status - extracted to reduce cognitive complexity (S3776)
function filterTeamsByStatus(teams: Team[], status: string): Team[] {
  if (status === 'default') {
    return teams.filter(t => t.isDefault);
  }
  if (status === 'custom') {
    return teams.filter(t => !t.isDefault);
  }
  if (status === 'no-members') {
    return teams.filter(t => t.memberCount === 0);
  }
  return teams;
}

// Helper function to filter teams by member count - extracted to reduce cognitive complexity (S3776)
function filterTeamsByMemberCount(teams: Team[], memberCount: string): Team[] {
  if (memberCount === 'large') {
    return teams.filter(t => t.memberCount >= 20);
  }
  if (memberCount === 'medium') {
    return teams.filter(t => t.memberCount >= 10 && t.memberCount < 20);
  }
  if (memberCount === 'small') {
    return teams.filter(t => t.memberCount >= 5 && t.memberCount < 10);
  }
  if (memberCount === 'tiny') {
    return teams.filter(t => t.memberCount >= 1 && t.memberCount < 5);
  }
  if (memberCount === 'empty') {
    return teams.filter(t => t.memberCount === 0);
  }
  return teams;
}

export function TeamsTable({
  teams,
  allUsers,
  permissions,
  filters,
  onFiltersChange,
  onTeamClick,
  onDeleteTeams,
}: Readonly<TeamsTableProps>) {
  const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, ] = useState<'name' | 'members' | 'modified'>('name');
    const [sortOrder, ] = useState<'asc' | 'desc'>('asc');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Filter teams using extracted helper functions to reduce cognitive complexity (S3776)
  const filteredTeams = useMemo(() => {
    // Apply status filter using helper function
    let result = filterTeamsByStatus([...teams], filters.status || 'all');

    // Apply member count filter using helper function
    result = filterTeamsByMemberCount(result, filters.memberCount || '');

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        t =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'members') {
        comparison = a.memberCount - b.memberCount;
      } else if (sortBy === 'modified') {
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Default teams always at top
    const defaultTeams = result.filter(t => t.isDefault);
    const customTeams = result.filter(t => !t.isDefault);
    return [...defaultTeams, ...customTeams];
  }, [teams, filters, searchQuery, sortBy, sortOrder]);

  const getMemberCountBadgeVariant = (count: number) => {
    if (count === 0) return 'destructive';
    if (count >= 20) return 'default';
    if (count >= 10) return 'secondary';
    return 'outline';
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.memberCount) count++;
    if (filters.recentlyModified) count++;
    return count;
  }, [filters]);

  const clearFilters = () => {
    onFiltersChange({});
    setSearchQuery('');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select non-default teams
      setSelectedTeamIds(filteredTeams.filter(t => !t.isDefault).map(t => t.id));
    } else {
      setSelectedTeamIds([]);
    }
  };

  const handleSelectTeam = (teamId: string, checked: boolean) => {
    if (checked) {
      setSelectedTeamIds([...selectedTeamIds, teamId]);
    } else {
      setSelectedTeamIds(selectedTeamIds.filter(id => id !== teamId));
    }
  };

  const handleDeleteSelected = () => {
    // Filter out default teams from selection
    const deletableTeamIds = selectedTeamIds.filter(id => {
      const team = teams.find(t => t.id === id);
      return team && !team.isDefault;
    });
    
    if (deletableTeamIds.length === 0) {
      // All selected teams are default teams - do nothing
      return;
    }
    
    // Update selection to only include deletable teams
    setSelectedTeamIds(deletableTeamIds);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDeleteTeams(selectedTeamIds);
    setSelectedTeamIds([]);
    setShowDeleteConfirm(false);
  };

  const handleViewUsers = () => {
    setShowUsersModal(true);
  };

  const handleViewPermissions = () => {
    setShowPermissionsModal(true);
  };

  // Pagination
  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage);
  const paginatedTeams = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTeams.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTeams, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [filteredTeams.length]);

  const selectedTeams = teams.filter(t => selectedTeamIds.includes(t.id));

  return (
    <div className="flex-1 flex overflow-hidden h-full">
      {/* Left Filter Panel */}
      <div className="w-64 border-r bg-muted/10 flex flex-col h-full">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-sm">Filters</h3>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-6">
            {/* Team Status */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                Team Status
              </h4>
              <div className="space-y-1">
                <Button
                  variant={!filters.status || filters.status === 'all' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, status: 'all' })}
                >
                  All Teams
                </Button>
                <Button
                  variant={filters.status === 'default' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, status: filters.status === 'default' ? undefined : 'default' })}
                >
                  Default Teams
                </Button>
                <Button
                  variant={filters.status === 'custom' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, status: filters.status === 'custom' ? undefined : 'custom' })}
                >
                  Custom Teams
                </Button>
                <Button
                  variant={filters.status === 'no-members' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start text-destructive font-normal"
                  onClick={() => onFiltersChange({ ...filters, status: filters.status === 'no-members' ? undefined : 'no-members' })}
                >
                  No Members
                </Button>
              </div>
            </div>

            {/* Member Count */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                Member Count
              </h4>
              <div className="space-y-1">
                <Button
                  variant={filters.memberCount === 'large' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, memberCount: filters.memberCount === 'large' ? undefined : 'large' })}
                >
                  20+ Members
                </Button>
                <Button
                  variant={filters.memberCount === 'medium' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, memberCount: filters.memberCount === 'medium' ? undefined : 'medium' })}
                >
                  10-19 Members
                </Button>
                <Button
                  variant={filters.memberCount === 'small' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, memberCount: filters.memberCount === 'small' ? undefined : 'small' })}
                >
                  5-9 Members
                </Button>
                <Button
                  variant={filters.memberCount === 'tiny' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, memberCount: filters.memberCount === 'tiny' ? undefined : 'tiny' })}
                >
                  1-4 Members
                </Button>
                <Button
                  variant={filters.memberCount === 'empty' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, memberCount: filters.memberCount === 'empty' ? undefined : 'empty' })}
                >
                  No Members
                </Button>
              </div>
            </div>

            {/* Recently Modified */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                Recently Modified
              </h4>
              <div className="space-y-1">
                <Button
                  variant={filters.recentlyModified === 'week' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, recentlyModified: 'week' })}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant={filters.recentlyModified === 'month' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, recentlyModified: 'month' })}
                >
                  Last 30 Days
                </Button>
                <Button
                  variant={filters.recentlyModified === 'quarter' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => onFiltersChange({ ...filters, recentlyModified: 'quarter' })}
                >
                  Last 90 Days
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>

        {activeFilterCount > 0 && (
          <div className="p-4 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={clearFilters}
            >
              <X className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden h-full">
        {/* Search and Actions Bar */}
        <div className="p-4 border-b flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Bulk Action Toolbar */}
        {selectedTeamIds.length > 0 && (
          <div className="px-4 py-3 border-b bg-primary/5 border-primary/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{selectedTeamIds.length} team{selectedTeamIds.length > 1 ? 's' : ''} selected</Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTeamIds([])}
              >
                Clear Selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {selectedTeamIds.length === 1 && (
                <>
                  <Button variant="outline" size="sm" onClick={handleViewPermissions}>
                    <Info className="h-4 w-4 mr-2" />
                    View Permissions
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleViewUsers}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Users
                  </Button>
                </>
              )}
              <Button variant="destructive" size="sm" onClick={handleDeleteSelected}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {selectedTeamIds.length > 1 ? `(${selectedTeamIds.length})` : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <ScrollArea className="flex-1">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader className="sticky top-0 bg-muted z-10">
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        filteredTeams.filter(t => !t.isDefault).length > 0 &&
                        selectedTeamIds.length === filteredTeams.filter(t => !t.isDefault).length
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="w-[30%]">Team Name</TableHead>
                  <TableHead className="w-[25%]">Description</TableHead>
                  <TableHead className="w-[15%]">Members</TableHead>
                  <TableHead className="w-[15%]">Permissions</TableHead>
                  <TableHead className="w-[10%]">Modified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white dark:bg-background">
                {paginatedTeams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      No teams found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTeams.map((team) => {
                  const IconComponent = TEAM_ICONS[team.icon] || Users;
                  const isSelected = selectedTeamIds.includes(team.id);
                  return (
                    <TableRow
                      key={team.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        isSelected && "bg-muted/50"
                      )}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectTeam(team.id, checked as boolean)}
                          disabled={team.isDefault}
                        />
                      </TableCell>
                      <TableCell onClick={() => onTeamClick(team)}>
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-md flex items-center justify-center"
                            style={{ backgroundColor: `${team.color}20`, color: team.color }}
                          >
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary hover:underline">
                                {team.name}
                              </span>
                              {team.isDefault && (
                                <Badge variant="outline" className="text-xs">
                                  Default Team
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm" onClick={() => onTeamClick(team)}>
                        <div className="line-clamp-2">{team.description}</div>
                      </TableCell>
                      <TableCell onClick={() => onTeamClick(team)}>
                        <Badge variant={getMemberCountBadgeVariant(team.memberCount)}>
                          {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => onTeamClick(team)}>
                        <Badge variant="secondary">
                          {team.permissions.filter(p => p.granted).length} enabled
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" onClick={() => onTeamClick(team)}>
                        {formatDistanceToNow(new Date(team.updatedAt), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
            </div>
          </ScrollArea>

          {/* Pagination Footer */}
          <div className="p-4 border-t flex items-center justify-between bg-white dark:bg-background">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredTeams.length)} of {filteredTeams.length} teams
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ViewTeamUsersModal
        open={showUsersModal}
        onOpenChange={setShowUsersModal}
        teams={selectedTeams}
        allUsers={allUsers}
      />

      <ViewTeamPermissionsModal
        open={showPermissionsModal}
        onOpenChange={setShowPermissionsModal}
        teams={selectedTeams}
        permissions={permissions}
      />

      <ConfirmationModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title={`Delete ${selectedTeamIds.length} Team${selectedTeamIds.length > 1 ? 's' : ''}?`}
        description={`Are you sure you want to delete ${selectedTeamIds.length === 1 ? 'this team' : 'these teams'}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
}
